// DeviceInstallPage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Inventory.css";
import config from "../config";              // 🟢 ADDED

// 🟢 use configured BASE_URL, fallback to old one if missing
const API_BASE = `${config.BASE_URL}/admin`;

const DeviceInstallPage = ({ createdBy = "AdminRaison" }) => {
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

  const [records, setRecords] = useState([]); // Rows from backend
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState(null); 

  const [editIndex, setEditIndex] = useState(null);                      
  const [editRow, setEditRow] = useState(null);  

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [confirmPopup, setConfirmPopup] = useState({
  open: false,
  title: "",
  message: "",
  variant: "confirm", // "confirm" | "info"
  onConfirm: null,
});

  // ================= GET /admin/all =================
 useEffect(() => {
  const fetchAll = async () => {
    try {
      setLoading(true);
      setListError("");
      const res = await axios.get(`${API_BASE}/all`);
      const data = Array.isArray(res.data) ? res.data : [];

      const normalized = data.map((item) => ({
        id: item.id,
        deviceId: item.deviceId || "",
        modelNumber: item.modelNumber || "",
        serialNumber: item.serialNumber || "",
        customerName: item.customerName || "",
        clientLocation: item.clientLocation || "",
        contactNumber: item.contactNumber || "",
        installedDate: item.installedDate || "",
        warrantyFrom: item.warrantyFrom || "",
        warrantyTo: item.warrantyTo || "",
        status: item.status || "",
        remarks: item.remarks || "",
        createdBy: item.createdBy || createdBy,
        createdDate: item.createdDate || today,
      }));

      setRecords(normalized);
    } catch (err) {
      console.error("Error fetching records:", err);
      setListError("Unable to load records from server.");
    } finally {
      setLoading(false);
    }
  };

  fetchAll();
}, [createdBy, today]);

// ================= ADD NEW ROW =================

const startAddRow = () => {
  if (isAdding || editIndex !== null) return;

  setIsAdding(true);
  setNewRow({
    deviceId: "",
    modelNumber: "",
    serialNumber: "",
    customerName: "",
    clientLocation: "",
    contactNumber: "",
    installedDate: "",
    warrantyFrom: "",
    warrantyTo: "",
    status: "",
    remarks: "",
    createdBy: createdBy,
    createdDate: today,
  });
};

const handleNewRowChange = (e) => {
  const { name, value } = e.target;

  setNewRow((prev) => {
    if (!prev) return prev;
    const updated = { ...prev, [name]: value };

    if (name === "installedDate") {
      if (value) {
        const start = new Date(value);
        if (!isNaN(start.getTime())) {
          const from = value;
          const toDate = new Date(start);
          toDate.setFullYear(toDate.getFullYear() + 1);
          updated.warrantyFrom = from;
          updated.warrantyTo = toDate.toISOString().split("T")[0];
        } else {
          updated.warrantyFrom = "";
          updated.warrantyTo = "";
        }
      } else {
        updated.warrantyFrom = "";
        updated.warrantyTo = "";
      }
    }

    return updated;
  });
};

const cancelAddRow = () => {
  setIsAdding(false);
  setNewRow(null);
};

// ================= SAVE NEW =================

const saveNewRow = async () => {
  if (
    !newRow.deviceId ||
    !newRow.modelNumber ||
    !newRow.serialNumber ||
    !newRow.customerName ||
    !newRow.status
  ) {
    openPopup({
      title: "Validation Error",
      message:
        "Please fill Device ID, Model Number, Serial Number, Customer Name and Status.",
      variant: "info",
    });
    return;
  }

  try {
    const res = await axios.post(`${API_BASE}/add`, newRow);
    const saved = res.data;

    const normalized = {
      ...saved,
      createdBy: saved.createdBy || newRow.createdBy,
      createdDate: saved.createdDate || newRow.createdDate,
    };

    setRecords((prev) => [...prev, normalized]);
    setIsAdding(false);
    setNewRow(null);

    openPopup({
      title: "Success",
      message: "Record added successfully!",
      variant: "info",
    });
  } catch (err) {
    console.error("Error saving new record:", err);
    openPopup({
      title: "Error",
      message: "Failed to save record. Please try again.",
      variant: "info",
    });
  }
};

// ================= POPUP =================

const openPopup = ({ title, message, variant = "info", onConfirm }) => {
  setConfirmPopup({
    open: true,
    title,
    message,
    variant,
    onConfirm: onConfirm || null,
  });
};

const closePopup = () => {
  setConfirmPopup((prev) => ({ ...prev, open: false }));
};

const handleConfirm = () => {
  if (confirmPopup.onConfirm) {
    confirmPopup.onConfirm();
  }
  closePopup();
};

// ================= EDIT =================

const startEditRow = (index) => {
  if (isAdding) return;
  setEditIndex(index);
  setEditRow({ ...records[index] });
};

const handleEditRowChange = (e) => {
  const { name, value } = e.target;

  setEditRow((prev) => {
    if (!prev) return prev;
    const updated = { ...prev, [name]: value };

    if (name === "installedDate") {
      if (value) {
        const start = new Date(value);
        if (!isNaN(start.getTime())) {
          const from = value;
          const toDate = new Date(start);
          toDate.setFullYear(toDate.getFullYear() + 1);
          updated.warrantyFrom = from;
          updated.warrantyTo = toDate.toISOString().split("T")[0];
        } else {
          updated.warrantyFrom = "";
          updated.warrantyTo = "";
        }
      } else {
        updated.warrantyFrom = "";
        updated.warrantyTo = "";
      }
    }

    return updated;
  });
};

const cancelEditRow = () => {
  setEditIndex(null);
  setEditRow(null);
};

// ================= UPDATE =================

const saveEditRow = async () => {
  if (
    !editRow.deviceId ||
    !editRow.modelNumber ||
    !editRow.serialNumber ||
    !editRow.customerName ||
    !editRow.status
  ) {
    openPopup({
      title: "Validation Error",
      message:
        "Please fill Device ID, Model Number, Serial Number, Customer Name and Status.",
      variant: "info",
    });
    return;
  }

  if (!editRow.id) {
    openPopup({
      title: "Error",
      message: "Cannot update: Record ID is missing.",
      variant: "info",
    });
    return;
  }

  try {
    await axios.put(`${API_BASE}/update/${editRow.deviceId}`, editRow);

    setRecords((prev) =>
      prev.map((rec, index) =>
        index === editIndex ? { ...rec, ...editRow } : rec
      )
    );

    setEditIndex(null);
    setEditRow(null);

    openPopup({
      title: "Success",
      message: "Record updated successfully!",
      variant: "info",
    });
  } catch (err) {
    console.error("Error updating record:", err);
    openPopup({
      title: "Error",
      message: "Failed to update record.",
      variant: "info",
    });
  }
};

// ================= DELETE =================

const deleteRow = (index) => {
  const row = records[index];

  if (!row.id) {
    openPopup({
      title: "Error",
      message: "Cannot delete: Record ID is missing.",
      variant: "info",
    });
    return;
  }

  openPopup({
    title: "Confirm Delete",
    message: `Are you sure you want to delete Device ID ${row.deviceId}?`,
    variant: "confirm",
    onConfirm: async () => {
      try {
        await axios.delete(`${API_BASE}/delete/${row.deviceId}`);
        setRecords((prev) => prev.filter((_, i) => i !== index));

        openPopup({
          title: "Success",
          message: "Record deleted successfully!",
          variant: "info",
        });
      } catch (err) {
        console.error(err);
        openPopup({
          title: "Error",
          message: "Failed to delete record.",
          variant: "info",
        });
      }
    },
  });
};
  return (
    <div className="inventory-container">
      <div className="inventory-header">
        <h1 className="inventory-title">Device Installation Details</h1>
        <button className="inventory-add-btn" onClick={startAddRow}>
          + Add
        </button>
      </div>

      {listError && <div className="inventory-error">{listError}</div>}
      {loading && <div className="inventory-loading">Loading...</div>}

      <div className="inventory-table-wrapper">
        <table className="inventory-table">
          <thead>
            <tr>
              <th className="inventory-th">Device ID</th>
              <th className="inventory-th">Model No</th>
              <th className="inventory-th">Serial No</th>
              <th className="inventory-th">Customer Name</th>
              <th className="inventory-th">Client Location</th>
              <th className="inventory-th">Contact No</th>
              <th className="inventory-th">Installed Date</th>
              <th className="inventory-th">Warranty From</th>
              <th className="inventory-th">Warranty To</th>
              <th className="inventory-th">Status</th>
              <th className="inventory-th">Remarks</th>
              <th className="inventory-th">Created By</th>
              <th className="inventory-th">Created Date</th>
              <th className="inventory-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Add row (in-line) */}
            {isAdding && newRow && (
              <tr>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="deviceId"
                    value={newRow.deviceId}
                    onChange={handleNewRowChange}
                    className="inventory-input"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="modelNumber"
                    value={newRow.modelNumber}
                    onChange={handleNewRowChange}
                    className="inventory-input"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="serialNumber"
                    value={newRow.serialNumber}
                    onChange={handleNewRowChange}
                    className="inventory-input"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="customerName"
                    value={newRow.customerName}
                    onChange={handleNewRowChange}
                    className="inventory-input"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="clientLocation"
                    value={newRow.clientLocation}
                    onChange={handleNewRowChange}
                    className="inventory-input inventory-input--wide"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="tel"
                    name="contactNumber"
                    value={newRow.contactNumber}
                    onChange={handleNewRowChange}
                    className="inventory-input"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="date"
                    name="installedDate"
                    value={newRow.installedDate}
                    onChange={handleNewRowChange}
                    className="inventory-input"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="date"
                    name="warrantyFrom"
                    value={newRow.warrantyFrom}
                    readOnly
                    className="inventory-input inventory-input--readonly"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="date"
                    name="warrantyTo"
                    value={newRow.warrantyTo}
                    readOnly
                    className="inventory-input inventory-input--readonly"
                  />
                </td>
                <td className="inventory-td">
                  <select
                    name="status"
                    value={newRow.status}
                    onChange={handleNewRowChange}
                    className="inventory-select"
                  >
                    <option value="">Select</option>
                    <option value="IN_STOCK">IN_STOCK</option>
                    <option value="INSTALLED">INSTALLED</option>
                    <option value="REPAIR">REPAIR</option>
                    <option value="RETURNED">RETURNED</option> 
                    <option value="SCRAPPED">SCRAPPED</option>
                  </select>
                </td>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="remarks"
                    value={newRow.remarks}
                    onChange={handleNewRowChange}
                    className="inventory-input inventory-input--wide"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="text"
                    name="createdBy"
                    value={newRow.createdBy}
                    readOnly
                    className="inventory-input inventory-input--readonly"
                  />
                </td>
                <td className="inventory-td">
                  <input
                    type="date"
                    name="createdDate"
                    value={newRow.createdDate}
                    readOnly
                    className="inventory-input inventory-input--readonly"
                  />
                </td>
                <td className="inventory-td">
                  <div className="inventory-actions">
                    <button
                      type="button"
                      onClick={saveNewRow}
                      className="inventory-action-btn inventory-action-btn--save"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={cancelAddRow}
                      className="inventory-action-btn inventory-action-btn--cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* No records */}
            {records.length === 0 &&
              !isAdding &&
              editIndex === null &&
              !loading && (
                <tr>
                  <td colSpan={14} className="inventory-td-empty">
                    No records added yet.
                  </td>
                </tr>
              )}

            {/* Existing rows */}
            {records.map((rec, index) => {
              // Edit mode row
              if (editIndex === index && editRow) {
                return (
                  <tr key={rec.id ?? index}>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="deviceId"
                        value={editRow.deviceId}
                        onChange={handleEditRowChange}
                        className="inventory-input"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="modelNumber"
                        value={editRow.modelNumber}
                        onChange={handleEditRowChange}
                        className="inventory-input"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="serialNumber"
                        value={editRow.serialNumber}
                        onChange={handleEditRowChange}
                        className="inventory-input"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="customerName"
                        value={editRow.customerName}
                        onChange={handleEditRowChange}
                        className="inventory-input"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="clientLocation"
                        value={editRow.clientLocation}
                        onChange={handleEditRowChange}
                        className="inventory-input inventory-input--wide"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="tel"
                        name="contactNumber"
                        value={editRow.contactNumber}
                        onChange={handleEditRowChange}
                        className="inventory-input"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="date"
                        name="installedDate"
                        value={editRow.installedDate}
                        onChange={handleEditRowChange}
                        className="inventory-input"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="date"
                        name="warrantyFrom"
                        value={editRow.warrantyFrom}
                        readOnly
                        className="inventory-input inventory-input--readonly"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="date"
                        name="warrantyTo"
                        value={editRow.warrantyTo}
                        readOnly
                        className="inventory-input inventory-input--readonly"
                      />
                    </td>
                    <td className="inventory-td">
                      <select
                        name="status"
                        value={editRow.status}
                        onChange={handleEditRowChange}
                        className="inventory-select"
                      >
                        <option value="">Select</option>
                        <option value="IN_STOCK">IN_STOCK</option>
                        <option value="INSTALLED">INSTALLED</option>
                        <option value="REPAIR">REPAIR</option>
                        <option value="RETURNED">RETURNED</option>
                        <option value="SCRAPPED">SCRAPPED</option>
                      </select>
                    </td>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="remarks"
                        value={editRow.remarks}
                        onChange={handleEditRowChange}
                        className="inventory-input inventory-input--wide"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="text"
                        name="createdBy"
                        value={editRow.createdBy}
                        readOnly
                        className="inventory-input inventory-input--readonly"
                      />
                    </td>
                    <td className="inventory-td">
                      <input
                        type="date"
                        name="createdDate"
                        value={editRow.createdDate}
                        readOnly
                        className="inventory-input inventory-input--readonly"
                      />
                    </td>
                    <td className="inventory-td">
                      <div className="inventory-actions">
                        <button
                          type="button"
                          onClick={saveEditRow}
                          className="inventory-action-btn inventory-action-btn--save"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditRow}
                          className="inventory-action-btn inventory-action-btn--cancel"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }

              // View mode row
              return (
                <tr key={rec.id ?? index}>
                  <td className="inventory-td">{rec.deviceId}</td>
                  <td className="inventory-td">{rec.modelNumber}</td>
                  <td className="inventory-td">{rec.serialNumber}</td>
                  <td className="inventory-td">{rec.customerName}</td>
                  <td className="inventory-td">{rec.clientLocation}</td>
                  <td className="inventory-td">{rec.contactNumber}</td>
                  <td className="inventory-td">{rec.installedDate}</td>
                  <td className="inventory-td">{rec.warrantyFrom}</td>
                  <td className="inventory-td">{rec.warrantyTo}</td>
                  <td className="inventory-td">{rec.status}</td>
                  <td className="inventory-td">{rec.remarks}</td>
                  <td className="inventory-td">{rec.createdBy}</td>
                  <td className="inventory-td">{rec.createdDate}</td>
                  <td className="inventory-td">
                    <div className="inventory-actions inventory-actions--row">
                      <button
                        type="button"
                        onClick={() => startEditRow(index)}
                        className="inventory-action-btn inventory-action-btn--update"
                      >
                        Update
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteRow(index)}
                        className="inventory-action-btn inventory-action-btn--delete"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
  {confirmPopup.open && (
  <div className="popup-overlay">
    <div className="popup-box popup-animate">

      {/* Close Button */}
      <button className="popup-close" onClick={closePopup}>
        ✕
      </button>

      {/* Title */}
      <h2 className="popup-title">
        {confirmPopup.title || "Confirm"}
      </h2>

      {/* Message */}
      <p className="popup-message">
        {confirmPopup.message || "Are you sure?"}
      </p>

      {/* Buttons */}
      <div className="popup-actions">
        {confirmPopup.variant === "info" ? (
          <button
            className="popup-button popup-basic"
            onClick={closePopup}
          >
            OK
          </button>
        ) : (
          <>
            <button
              className="popup-button popup-basic"
              onClick={closePopup}
            >
              Cancel
            </button>

            <button
              className="popup-button popup-error"
              onClick={handleConfirm}
            >
              Confirm
            </button>
          </>
        )}
      </div>

    </div>
  </div>
)}
    </div>
  );

};

export default DeviceInstallPage;